import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useLatest } from "react-use";

import { LoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper";
import type { Deferred } from "metabase/lib/promise";
import { defer } from "metabase/lib/promise";
import { useSelector } from "metabase/lib/redux";
import { uuid } from "metabase/lib/uuid";
import { getMetadata } from "metabase/selectors/metadata";
import { getUserId } from "metabase/selectors/user";
import { runQuestionQuery } from "metabase/services";
import * as Lib from "metabase-lib";
import type {
  CardDisplayType,
  DatabaseId,
  Dataset,
  VisualizationSettings,
} from "metabase-types/api";

import { useAdhocPersistence } from "./hooks/use-adhoc-persistence";
import {
  DEFAULT_RESULT_DISPLAY,
  DEFAULT_RESULT_SETTINGS,
  createInitialState,
  workbenchReducer,
} from "./state/reducer";
import { getRunningResultIdsForTab, getTab } from "./state/selectors";
import type {
  AdhocResultId,
  AdhocTabId,
  NewTabOptions,
  PersistedWorkbench,
  WorkbenchState,
} from "./types";
import { createNativeQuestion } from "./utils/question";
import { loadWorkbench } from "./utils/storage";

/**
 * A dataset-shaped object describing a failed request.
 *
 * `/api/dataset` reports SQL errors inside a normal response body, so only
 * transport-level failures reach us as a rejection. The query builder stores
 * those as `{ error, duration }` (see `queryErrored` in
 * `metabase/query_builder/actions/querying`), and `VisualizationError` reads
 * exactly those two fields, so we synthesize the same shape here.
 */
function createErrorDataset(error: unknown, duration: number): Dataset {
  return { error, duration } as unknown as Dataset;
}

export type WorkbenchActions = {
  addTab: (options?: NewTabOptions) => void;
  duplicateTab: (tabId: AdhocTabId) => void;
  closeTab: (tabId: AdhocTabId) => void;
  closeOtherTabs: (tabId: AdhocTabId) => void;
  renameTab: (tabId: AdhocTabId, name: string) => void;
  selectTab: (tabId: AdhocTabId) => void;
  moveTab: (activeId: AdhocTabId, overId: AdhocTabId) => void;
  setTabQuery: (
    tabId: AdhocTabId,
    databaseId: DatabaseId | null,
    sql: string,
  ) => void;
  setTabEditorHeight: (tabId: AdhocTabId, height: number) => void;
  runQuery: (tabId: AdhocTabId, sql: string) => void;
  cancelResult: (resultId: AdhocResultId) => void;
  closeResult: (tabId: AdhocTabId, resultId: AdhocResultId) => void;
  selectResult: (tabId: AdhocTabId, resultId: AdhocResultId) => void;
  setResultDisplay: (
    tabId: AdhocTabId,
    resultId: AdhocResultId,
    display: CardDisplayType,
    visualizationSettings: VisualizationSettings,
  ) => void;
  setResultSettings: (
    tabId: AdhocTabId,
    resultId: AdhocResultId,
    visualizationSettings: VisualizationSettings,
  ) => void;
};

export const WorkbenchStateContext = createContext<WorkbenchState | null>(null);

/**
 * Kept separate from the state context so components that only need callbacks
 * (tab bar buttons, the editor toolbar) don't re-render on every keystroke.
 */
export const WorkbenchActionsContext = createContext<WorkbenchActions | null>(
  null,
);

type AdhocWorkbenchProviderProps = {
  children: ReactNode;
  /** Database to seed the very first tab with, when there is nothing to restore. */
  defaultDatabaseId?: DatabaseId | null;
};

/**
 * Reads the stored workbench before mounting anything that depends on it.
 *
 * Restoring is asynchronous (IndexedDB), and the reducer needs its initial
 * state on the very first render, so the two stages are split: this one waits,
 * the inner one owns the state. Waiting rather than hydrating afterwards also
 * keeps the invariant that a tab panel never first mounts hidden.
 */
export function AdhocWorkbenchProvider({
  children,
  defaultDatabaseId = null,
}: AdhocWorkbenchProviderProps) {
  const userId = useSelector(getUserId);
  const [restored, setRestored] = useState<PersistedWorkbench | null>();

  useEffect(() => {
    let isCurrent = true;
    loadWorkbench(userId).then((workbench) => {
      if (isCurrent) {
        setRestored(workbench);
      }
    });
    return () => {
      isCurrent = false;
    };
  }, [userId]);

  if (restored === undefined) {
    return <LoadingAndErrorWrapper loading />;
  }

  return (
    <HydratedWorkbenchProvider
      restored={restored}
      userId={userId}
      defaultDatabaseId={defaultDatabaseId}
    >
      {children}
    </HydratedWorkbenchProvider>
  );
}

type HydratedWorkbenchProviderProps = AdhocWorkbenchProviderProps & {
  restored: PersistedWorkbench | null;
  userId: number | undefined;
};

function HydratedWorkbenchProvider({
  children,
  restored,
  userId,
  defaultDatabaseId = null,
}: HydratedWorkbenchProviderProps) {
  const metadata = useSelector(getMetadata);
  const metadataRef = useLatest(metadata);

  const [state, dispatch] = useReducer(
    workbenchReducer,
    restored,
    createInitialState,
  );
  const stateRef = useLatest(state);

  /**
   * Cancellation handles for in-flight runs, keyed by result id.
   *
   * These are mutable and non-serializable, so they live in a ref rather than
   * in the reducer state.
   */
  const cancelDeferreds = useRef(new Map<AdhocResultId, Deferred>());

  const { flush: flushPersistence } = useAdhocPersistence(state, userId);

  const cancelResult = useCallback((resultId: AdhocResultId) => {
    // Resolving the deferred aborts the underlying XHR (see `metabase/lib/api`).
    cancelDeferreds.current.get(resultId)?.resolve();
  }, []);

  const runQuery = useCallback(
    async (tabId: AdhocTabId, sql: string) => {
      const tab = getTab(stateRef.current, tabId);
      const databaseId = tab?.databaseId;
      if (!tab || sql.trim() === "" || databaseId == null) {
        return;
      }

      // Snapshot the data source and SQL as executed. Editing the tab
      // afterwards must not change what this result displays or exports.
      const question = createNativeQuestion({
        databaseId,
        sql,
        metadata: metadataRef.current,
        display: DEFAULT_RESULT_DISPLAY,
        visualizationSettings: DEFAULT_RESULT_SETTINGS,
      });
      if (!Lib.canRun(question.query(), question.type())) {
        return;
      }

      // Running is the checkpoint people expect to survive a reload, so don't
      // leave the SQL sitting in the debounce window.
      flushPersistence();

      const resultId = uuid();
      const startedAt = Date.now();
      dispatch({
        type: "RESULT_STARTED",
        payload: { tabId, resultId, databaseId, sql, startedAt },
      });

      const cancelDeferred = defer();
      cancelDeferreds.current.set(resultId, cancelDeferred);
      try {
        const [dataset] = await runQuestionQuery(question, {
          cancelDeferred,
          isDirty: true,
        });
        dispatch({
          type: "RESULT_FINISHED",
          payload: { tabId, resultId, dataset, finishedAt: Date.now() },
        });
      } catch (error) {
        if ((error as { isCancelled?: boolean })?.isCancelled) {
          dispatch({
            type: "RESULT_CANCELLED",
            payload: { tabId, resultId, finishedAt: Date.now() },
          });
        } else {
          dispatch({
            type: "RESULT_FAILED",
            payload: {
              tabId,
              resultId,
              dataset: createErrorDataset(error, Date.now() - startedAt),
              finishedAt: Date.now(),
            },
          });
        }
      } finally {
        cancelDeferreds.current.delete(resultId);
      }
    },
    [flushPersistence, metadataRef, stateRef],
  );

  const actions = useMemo<WorkbenchActions>(
    () => ({
      addTab: ({ databaseId, sql }: NewTabOptions = {}) => {
        const current = getTab(stateRef.current, stateRef.current.activeTabId);
        dispatch({
          type: "ADD_TAB",
          payload: {
            id: uuid(),
            // Inheriting the current data source saves a click, and matches
            // what people expect coming from other SQL workbenches.
            databaseId: databaseId ?? current?.databaseId ?? null,
            sql,
          },
        });
      },
      duplicateTab: (tabId) =>
        dispatch({
          type: "DUPLICATE_TAB",
          payload: { id: tabId, newId: uuid() },
        }),
      closeTab: (tabId) => {
        // Nothing renders a closed tab's results, so letting its queries run on
        // would just burn a database connection.
        getRunningResultIdsForTab(stateRef.current, tabId).forEach(
          cancelResult,
        );
        dispatch({ type: "CLOSE_TAB", payload: { id: tabId } });
      },
      closeOtherTabs: (tabId) => {
        stateRef.current.tabs
          .filter((tab) => tab.id !== tabId)
          .forEach((tab) =>
            getRunningResultIdsForTab(stateRef.current, tab.id).forEach(
              cancelResult,
            ),
          );
        dispatch({ type: "CLOSE_OTHER_TABS", payload: { id: tabId } });
      },
      renameTab: (tabId, name) =>
        dispatch({ type: "RENAME_TAB", payload: { id: tabId, name } }),
      selectTab: (tabId) =>
        dispatch({ type: "SELECT_TAB", payload: { id: tabId } }),
      moveTab: (activeId, overId) =>
        dispatch({ type: "MOVE_TAB", payload: { activeId, overId } }),
      setTabQuery: (tabId, databaseId, sql) =>
        dispatch({
          type: "SET_TAB_QUERY",
          payload: { id: tabId, databaseId, sql },
        }),
      setTabEditorHeight: (tabId, height) =>
        dispatch({
          type: "SET_TAB_EDITOR_HEIGHT",
          payload: { id: tabId, height },
        }),
      runQuery,
      cancelResult,
      closeResult: (tabId, resultId) => {
        cancelResult(resultId);
        dispatch({ type: "CLOSE_RESULT", payload: { tabId, resultId } });
      },
      selectResult: (tabId, resultId) =>
        dispatch({ type: "SELECT_RESULT", payload: { tabId, resultId } }),
      setResultDisplay: (tabId, resultId, display, visualizationSettings) =>
        dispatch({
          type: "SET_RESULT_DISPLAY",
          payload: { tabId, resultId, display, visualizationSettings },
        }),
      setResultSettings: (tabId, resultId, visualizationSettings) =>
        dispatch({
          type: "SET_RESULT_SETTINGS",
          payload: { tabId, resultId, visualizationSettings },
        }),
    }),
    [cancelResult, runQuery, stateRef],
  );

  // Seed the first tab's database when there was nothing to restore.
  const hasSeededDatabase = useRef(false);
  useEffect(() => {
    const tab = getTab(stateRef.current, stateRef.current.activeTabId);
    if (
      !hasSeededDatabase.current &&
      defaultDatabaseId != null &&
      tab != null &&
      tab.databaseId == null
    ) {
      hasSeededDatabase.current = true;
      actions.setTabQuery(tab.id, defaultDatabaseId, tab.sql);
    }
  }, [actions, defaultDatabaseId, stateRef]);

  // Abort anything still in flight when the workbench unmounts.
  useEffect(() => {
    const deferreds = cancelDeferreds.current;
    return () => {
      deferreds.forEach((deferred) => deferred.resolve());
      deferreds.clear();
    };
  }, []);

  return (
    <WorkbenchStateContext.Provider value={state}>
      <WorkbenchActionsContext.Provider value={actions}>
        {children}
      </WorkbenchActionsContext.Provider>
    </WorkbenchStateContext.Provider>
  );
}

export function useWorkbenchState(): WorkbenchState {
  const state = useContext(WorkbenchStateContext);
  if (state == null) {
    throw new Error(
      "useWorkbenchState must be used inside AdhocWorkbenchProvider",
    );
  }
  return state;
}

export function useWorkbenchActions(): WorkbenchActions {
  const actions = useContext(WorkbenchActionsContext);
  if (actions == null) {
    throw new Error(
      "useWorkbenchActions must be used inside AdhocWorkbenchProvider",
    );
  }
  return actions;
}
